import { useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmt, printInvoice } from "@/lib/billing";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Trash2, Printer, Wallet, FileText, Truck, Pencil } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PaymentDialog, StatusBadge } from "@/components/InvoicePayment";
import { CompanyDialog } from "@/components/CompanyDialog";
import { SendMailDialog, MailButton, mailTargets } from "@/components/SendMail";
import { LineItemsEditor, InvoiceHeaderFields, NotesField, useBillingData, emptyInvoice, newLine } from "@/components/InvoiceForm";

export default function Invoices() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const { parties, items, reload } = useBillingData();
  const [invoices, setInvoices] = useState([]);
  const [summary, setSummary] = useState(null);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(emptyInvoice());
  const [lines, setLines] = useState([newLine()]);
  const [payFor, setPayFor] = useState(null);
  const [mailFor, setMailFor] = useState(null);
  const [editing, setEditing] = useState(null);

  const openEdit = (inv) => {
    setEditing(inv);
    setF({ invoice_no: inv.invoice_no, date: inv.date, due_date: inv.due_date || "", party_id: inv.party_id, vendor_id: "", notes: inv.notes || "" });
    setLines(inv.items.map((l) => ({ item_id: l.item_id, description: l.description || "", quantity: String(l.quantity), rate: String(l.rate), gst_pct: String(l.gst_pct) })));
    reload(); setOpen(true);
  };
  const [shipFor, setShipFor] = useState(null);
  const [ship, setShip] = useState({ shipped_date: "", shipped_ref: "" });

  const openShip = (inv) => { setShipFor(inv); setShip({ shipped_date: inv.shipped_date || new Date().toISOString().slice(0, 10), shipped_ref: inv.shipped_ref || "" }); };
  const setDelivery = async (inv, status) => {
    try {
      await api.put(`/invoices/${inv.id}/delivery`, { delivery_status: status, ...ship });
      toast.success(status === "material_shipped" ? "Marked as Material Shipped" : "Marked as Invoice Raised");
      setShipFor(null); load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const load = () => {
    api.get("/invoices").then((r) => setInvoices(r.data)).catch((e) => toast.error(errMsg(e)));
    api.get("/billing/summary").then((r) => setSummary(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!f.party_id) return toast.error("Select a customer");
    if (lines.some((l) => !l.item_id)) return toast.error("Select an item on every line");
    try {
      const payload = {
        invoice_no: f.invoice_no, date: f.date, due_date: f.due_date, party_id: f.party_id, notes: f.notes,
        items: lines.map((l) => ({ item_id: l.item_id, description: l.description, quantity: parseFloat(l.quantity), rate: parseFloat(l.rate), gst_pct: parseFloat(l.gst_pct) })),
      };
      const r = editing ? await api.put(`/invoices/${editing.id}`, payload) : await api.post("/invoices", payload);
      toast.success(editing ? `Invoice ${r.data.invoice_no} updated` : `Invoice ${r.data.invoice_no} created`);
      setOpen(false); setEditing(null); setF(emptyInvoice()); setLines([newLine()]); load(); reload();
      if (!editing) printInvoice(r.data);
    } catch (err) { toast.error(errMsg(err)); }
  };
  const del = async (id) => {
    try { await api.delete(`/invoices/${id}`); toast.success("Deleted"); load(); } catch (err) { toast.error(errMsg(err)); }
  };
  const customers = parties.filter((p) => p.party_type !== "vendor");

  return (
    <div className="space-y-6 animate-fade-up" data-testid="invoices-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight">Sales Invoices</h1>
          <p className="text-muted-foreground mt-1">GST invoices to customers and receipts against them.</p>
        </div>
        <div className="flex gap-2">
        <CompanyDialog />
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) reload(); else setEditing(null); }}>
          <DialogTrigger asChild>
            <Button data-testid="add-invoice-btn" onClick={() => { setEditing(null); setF(emptyInvoice()); setLines([newLine()]); }} className="bg-sky-500 hover:bg-sky-600 text-white gap-2"><Plus className="h-4 w-4" /> New Invoice</Button>
          </DialogTrigger>
          <DialogContent className="bg-card border-border max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{editing ? `Edit Invoice ${editing.invoice_no}` : "New Sales Invoice"}</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-4">
              <InvoiceHeaderFields f={f} setF={setF} parties={customers} partyKey="party_id" partyLabel="Customer" prefix="inv" autoNo />
              <LineItemsEditor lines={lines} setLines={setLines} items={items} prefix="inv" />
              <NotesField f={f} setF={setF} prefix="inv" />
              <DialogFooter><Button type="submit" data-testid="inv-submit-btn" className="bg-sky-500 hover:bg-sky-600 text-white gap-2">{editing ? "Update Invoice" : <><Printer className="h-4 w-4" /> Save & Print</>}</Button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {summary && (
        <div className="grid grid-cols-3 gap-4">
          <Card className="bg-card border-border"><CardContent className="p-4"><p className="text-xs uppercase text-muted-foreground font-mono-sku">Total Billed</p><p className="text-2xl font-extrabold" data-testid="inv-total-billed">{fmt(summary.sales_total)}</p><p className="text-xs text-muted-foreground">{summary.sales_count} invoices</p></CardContent></Card>
          <Card className="bg-emerald-500/10 border-emerald-500/20"><CardContent className="p-4"><p className="text-xs uppercase text-muted-foreground font-mono-sku">Received</p><p className="text-2xl font-extrabold text-emerald-400">{fmt(summary.sales_received)}</p></CardContent></Card>
          <Card className="bg-amber-500/10 border-amber-500/20"><CardContent className="p-4"><p className="text-xs uppercase text-muted-foreground font-mono-sku">Receivable (Due)</p><p className="text-2xl font-extrabold text-amber-400" data-testid="inv-receivable">{fmt(summary.receivable)}</p></CardContent></Card>
        </div>
      )}

      <Card className="bg-card border-border"><CardContent className="p-0 overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-border text-left text-muted-foreground">
            <th className="px-4 py-3 font-medium">Invoice</th><th className="px-4 py-3 font-medium">Date</th><th className="px-4 py-3 font-medium">Customer</th>
            <th className="px-4 py-3 font-medium">GSTIN</th><th className="px-4 py-3 font-medium text-right">Taxable</th><th className="px-4 py-3 font-medium text-right">GST</th>
            <th className="px-4 py-3 font-medium text-right">Total</th><th className="px-4 py-3 font-medium text-right">Balance</th><th className="px-4 py-3 font-medium">Payment</th><th className="px-4 py-3 font-medium">Shipped / Raised</th><th className="px-4 py-3"></th>
          </tr></thead>
          <tbody data-testid="invoices-table-body">
            {invoices.length === 0 ? (
              <tr><td colSpan={11} className="px-4 py-12 text-center text-muted-foreground"><FileText className="h-10 w-10 mx-auto mb-2 opacity-40" />No invoices yet.</td></tr>
            ) : invoices.map((inv) => (
              <tr key={inv.id} className="border-b border-border/50 hover:bg-white/5" data-testid={`invoice-row-${inv.id}`}>
                <td className="px-4 py-3 font-mono-sku font-semibold text-sky-300">{inv.invoice_no}</td>
                <td className="px-4 py-3 font-mono-sku text-xs">{inv.date}</td>
                <td className="px-4 py-3 font-medium">{inv.party_name}</td>
                <td className="px-4 py-3 font-mono-sku text-xs text-muted-foreground">{inv.party?.gstin || "—"}</td>
                <td className="px-4 py-3 text-right font-mono-sku">{fmt(inv.subtotal)}</td>
                <td className="px-4 py-3 text-right font-mono-sku text-muted-foreground">{fmt(inv.gst_total)}</td>
                <td className="px-4 py-3 text-right font-mono-sku font-semibold">{fmt(inv.total)}</td>
                <td className="px-4 py-3 text-right font-mono-sku text-amber-400" data-testid={`invoice-balance-${inv.id}`}>{inv.balance > 0 ? fmt(inv.balance) : "—"}</td>
                <td className="px-4 py-3"><StatusBadge status={inv.status} /></td>
                <td className="px-4 py-3">
                  <button type="button" data-testid={`invoice-delivery-${inv.id}`} onClick={() => openShip(inv)} title="Click to change"
                    className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium ${inv.delivery_status === "material_shipped" ? "text-emerald-400 border-emerald-500/30 bg-emerald-500/10" : "text-violet-300 border-violet-500/30 bg-violet-500/10"}`}>
                    <Truck className="h-3 w-3" /> {inv.delivery_status === "material_shipped" ? `Material Shipped${inv.shipped_date ? ` · ${inv.shipped_date}` : ""}` : "Invoice Raised"}
                  </button>
                </td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  {inv.balance > 0 && <Button size="sm" variant="ghost" data-testid={`invoice-receive-${inv.id}`} onClick={() => setPayFor(inv)} className="h-8 gap-1 text-emerald-400 hover:bg-emerald-500/10"><Wallet className="h-4 w-4" /> Receive</Button>}
                  <Button size="icon" variant="ghost" data-testid={`invoice-print-${inv.id}`} onClick={() => printInvoice(inv)} className="h-8 w-8"><Printer className="h-4 w-4" /></Button>
                  <MailButton testid={`invoice-mail-${inv.id}`} onClick={() => setMailFor(mailTargets.invoice(inv, inv.company?.name))} />
                  {isAdmin && <Button size="icon" variant="ghost" data-testid={`invoice-edit-${inv.id}`} onClick={() => openEdit(inv)} className="h-8 w-8 text-sky-400 hover:bg-sky-500/10"><Pencil className="h-4 w-4" /></Button>}
                  {isAdmin && <Button size="icon" variant="ghost" data-testid={`invoice-delete-${inv.id}`} onClick={() => del(inv.id)} className="h-8 w-8 text-rose-400 hover:bg-rose-500/10"><Trash2 className="h-4 w-4" /></Button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent></Card>

      <PaymentDialog invoice={payFor} endpoint="/invoices" label="Receipt" onClose={() => setPayFor(null)} onDone={() => { setPayFor(null); load(); }} />
      <SendMailDialog target={mailFor} onClose={() => setMailFor(null)} />

      <Dialog open={!!shipFor} onOpenChange={(o) => !o && setShipFor(null)}>
        <DialogContent className="bg-card border-border">
          <DialogHeader><DialogTitle>Delivery Status · {shipFor?.invoice_no}</DialogTitle></DialogHeader>
          {shipFor && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label>Shipped Date</Label>
                  <Input data-testid="ship-date-input" type="date" value={ship.shipped_date} onChange={(e) => setShip({ ...ship, shipped_date: e.target.value })} /></div>
                <div className="space-y-2"><Label>Gate Pass / Vehicle / Ref</Label>
                  <Input data-testid="ship-ref-input" value={ship.shipped_ref} onChange={(e) => setShip({ ...ship, shipped_ref: e.target.value })} placeholder="GP-12 / UP32 AB 1234" /></div>
              </div>
              <DialogFooter className="gap-2">
                <Button variant="outline" data-testid="ship-mark-raised-btn" onClick={() => setDelivery(shipFor, "invoice_raised")} className="border-border">Invoice Raised (not shipped)</Button>
                <Button data-testid="ship-mark-shipped-btn" onClick={() => setDelivery(shipFor, "material_shipped")} className="bg-emerald-500 hover:bg-emerald-600 text-white gap-2"><Truck className="h-4 w-4" /> Material Shipped</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
